import React, { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { getFamilyTreeIdFromStorage } from "../../../Utils/headOfLineRole";
import {
  getAnniversaryCoupleTrailingName,
  getAnniversaryPersonNameParts,
  isDashboardInLawMember,
} from "../../../Utils/dashboardMemberDisplayName";
import { isExplicitDashboardStartOrTopOfLineMember } from "../../../Utils/dashboardStartOfLineMember";
import {
  DashboardPhotoHeadLine,
  DashboardPhotoModal,
  DashboardPhotoName,
  DashboardMemberNameLabel,
  DashboardInLawAsterisk,
  DashboardNameMarkerGroup,
  getAnniversaryPersonPhotoUrls,
  getDashboardMemberPhotoUrl,
  useDashboardPhotoModal,
} from "./DashboardPhotoViewer";
import DashboardBackButton from "./DashboardBackButton";
import DashboardColorLegend from "./DashboardColorLegend";
import DashboardDetailSearchBox, {
  DashboardDetailSearchNoMatch,
} from "./DashboardDetailSearchBox";
import DashboardDetailSortFilterBar from "./DashboardDetailSortFilterBar";
import DashboardMarkerLegend from "./DashboardMarkerLegend";
import DashboardPhotoTapHint from "./DashboardPhotoTapHint";
import DashboardPeriodDropdown from "./DashboardPeriodDropdown";
import {
  buildDashboardColorLegendLines,
  buildHeadsOfLineIdByColorMap,
  getDashboardFamilyLineHeadDisplayName,
  extractAllMembersFromStatisticsResponse,
  extractHeadsOfLineFromStatisticsResponse,
} from "../../../Utils/dashboardFamilyLineNavigation";
import { getApi } from "../../../Services/apiService";
import { getDashboardAnniversaryMonthDay } from "../../../Utils/dashboardPeriodMonth";
import { fetchMilestoneWeddingAnniversaries } from "../../../Utils/dashboardMilestoneAnniversaries";
import { filterDashboardMembersBySearch } from "../../../Utils/dashboardDetailSearch";

/**
 * Annual Milestone Wedding Anniversaries — grouped by family line colour, with
 * the same three data columns and spacing as Wedding Anniv this Month: couple
 * names, then the wedding date, then the milestone year.
 */

const ANNIVERSARY_GEMS = [
  [25, "Silver"], [30, "Pearl"], [35, "Coral/Jade"], [40, "Ruby"],
  [45, "Sapphire"], [50, "Gold"], [55, "Emerald"], [60, "Diamond"],
];

// Each year and its gem sit in one span so a wrap never splits them apart.
const ANNIVERSARY_GEMS_NOTE = (
  <>
    {"Starting from 25 up to 60 years here are the associated gems:  "}
    {ANNIVERSARY_GEMS.map(([years, gem], i) => (
      <React.Fragment key={years}>
        {i === 0 ? "" : i === ANNIVERSARY_GEMS.length - 1 ? ", and " : ", "}
        <span className="anniversaries-gems-pair">{`${years}  ${gem}`}</span>
      </React.Fragment>
    ))}
  </>
);

const YEAR_PICKER_SPAN = 5;

const DEFAULT_SORT = "alpha-asc";

const AMPERSAND_SPLIT_RE = /\s*&\s*/;

function sanitizeAmpersandChars(text) {
  if (text == null || typeof text !== "string") return "";
  return text
    .replace(/\uFF06/g, "&")
    .replace(/\uFE60/g, "&");
}

function normalizeAmpersandSpacing(text) {
  if (text == null || typeof text !== "string") return "";
  return text
    .split(AMPERSAND_SPLIT_RE)
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .join(" & ");
}

function rawCoupleStrings(anniversary) {
  const first =
    anniversary?.first_name ??
    anniversary?.firstName ??
    "";
  const spouse =
    anniversary?.spouse_first_name ??
    anniversary?.spouseFirstName ??
    "";
  return {
    firstName: sanitizeAmpersandChars(String(first)).trim(),
    spouseFirstName: sanitizeAmpersandChars(String(spouse)).trim(),
  };
}

function getCoupleFirstNames(anniversary) {
  let { firstName, spouseFirstName } = rawCoupleStrings(anniversary);

  if (firstName.includes("&")) {
    const parts = firstName.split(AMPERSAND_SPLIT_RE).map((p) => p.trim()).filter(Boolean);
    if (parts.length >= 2) {
      firstName = parts[0];
      spouseFirstName = parts.slice(1).join(" & ");
    }
  }

  return {
    firstName: normalizeAmpersandSpacing(firstName),
    spouseFirstName: normalizeAmpersandSpacing(spouseFirstName),
  };
}

const getAnniversaryDayNum = (row) =>
  getDashboardAnniversaryMonthDay(row)?.day ?? -1;

const getAnniversaryMonthNum = (row) =>
  getDashboardAnniversaryMonthDay(row)?.month ?? 12;

function getCoupleSortName(row) {
  const { firstName, spouseFirstName } = getCoupleFirstNames(row);
  return `${firstName} & ${spouseFirstName}`.toLowerCase();
}

function getCoupleSearchName(row) {
  return [
    row?.member_name,
    row?.spouse_name,
    row?.first_name,
    row?.last_name,
    row?.spouse_first_name,
    row?.spouse_last_name,
    row?.color_name,
  ]
    .filter(Boolean)
    .join(" ");
}

/** Wedding date as M/D/YYYY — the format the printed milestone report uses. */
function getMilestoneWeddingDateDisplay(row) {
  const formatted = String(row?.wedding_date_formatted || "").trim();
  if (formatted) return formatted;

  const parts = getDashboardAnniversaryMonthDay(row);
  const weddingYear = row?.wedding_year;
  if (!parts || weddingYear == null || weddingYear === "") return "";

  return `${parts.month + 1}/${parts.day}/${weddingYear}`;
}

const recordId = (record) => {
  const id = record?.id ?? record?.member_id;
  return id == null || id === "" ? "" : String(id);
};

function MilestoneWeddingAnniversaries() {
  const { photos: photoModalPhotos, openPhoto, closePhoto } = useDashboardPhotoModal();
  const [headsMap, setHeadsMap] = useState(() => buildHeadsOfLineIdByColorMap([]));
  const [rosterMembers, setRosterMembers] = useState([]);
  const [rosterById, setRosterById] = useState(() => new Map());
  const [sections, setSections] = useState([]);
  const [reportTotal, setReportTotal] = useState(0);
  const [reportLoading, setLoading] = useState(true);
  const [rosterLoading, setRosterLoading] = useState(true);
  const now = useMemo(() => new Date(), []);
  const [year, setYear] = useState(now.getFullYear());
  const [nameColumnWidth, setNameColumnWidth] = useState("auto");
  const [dateColumnWidth, setDateColumnWidth] = useState("auto");
  const [yearsColumnWidth, setYearsColumnWidth] = useState("auto");
  const [yearsNumColumnWidth, setYearsNumColumnWidth] = useState("auto");
  // Opens A–Z; that button stays grayed out until another sort takes over.
  const [sortBy, setSortBy] = useState(DEFAULT_SORT);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLargeView] = useState(false);
  const [isCompactLayout, setIsCompactLayout] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 768
  );
  // The phone's footer key comes from the roster, a separate and slower call. A
  // report opened ahead of it showed a short legend that grew moments later, so
  // the phone holds the page for it; a failed roster call still lets it open.
  const loading = reportLoading || (isCompactLayout && rosterLoading);
  const listRef = useRef(null);

  useEffect(() => {
    const updateLayoutMode = () => setIsCompactLayout(window.innerWidth <= 768);
    updateLayoutMode();
    window.addEventListener("resize", updateLayoutMode);
    return () => window.removeEventListener("resize", updateLayoutMode);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchMilestones = async () => {
      try {
        setLoading(true);
        const family_tree_id = getFamilyTreeIdFromStorage();

        if (!family_tree_id) {
          setSections([]);
          setReportTotal(0);
          return;
        }

        const report = await fetchMilestoneWeddingAnniversaries(family_tree_id, { year });
        if (cancelled) return;
        setSections(report.sections);
        setReportTotal(report.totalCount);
      } catch (error) {
        console.error("Error fetching milestone wedding anniversaries:", error);
        if (!cancelled) {
          setSections([]);
          setReportTotal(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchMilestones();
    return () => {
      cancelled = true;
    };
  }, [year]);

  useEffect(() => {
    const fetchRoster = async () => {
      try {
        const family_tree_id = getFamilyTreeIdFromStorage();
        if (!family_tree_id) return;

        const response = await getApi(
          `/dashboard-statistics-with-details?family_tree_id=${family_tree_id}`
        );
        if (response?.statusCode === 200 && response?.data) {
          setHeadsMap(
            buildHeadsOfLineIdByColorMap(
              extractHeadsOfLineFromStatisticsResponse(response.data),
              extractAllMembersFromStatisticsResponse(response.data)
            )
          );
          const members = extractAllMembersFromStatisticsResponse(response.data);
          setRosterMembers(members);
          setRosterById(
            new Map(
              members
                .map((member) => [recordId(member), member])
                .filter(([id]) => id)
            )
          );
        }
      } catch (error) {
        console.error("Error fetching family lines:", error);
      } finally {
        setRosterLoading(false);
      }
    };

    fetchRoster();
  }, []);

  /**
   * Dress each report row from the roster the way Wedding Anniv this Month does,
   * so suffix, special name and spouse fields read the same couple string.
   */
  const resolveAnniversaryRow = useCallback(
    (row) => {
      const roster = rosterById.get(recordId(row));
      const merged = { ...row, ...(roster || {}) };
      merged.years = row?.years ?? row?.years_married ?? merged.years;
      merged.years_married = row?.years_married ?? merged.years_married;
      merged.wedding_date_formatted =
        row?.wedding_date_formatted ?? merged.wedding_date_formatted;
      merged.wedding_year = row?.wedding_year ?? merged.wedding_year;
      merged.color_name =
        row?.color_name || merged.color_name || merged.family_line_color || "";
      merged.hex_code =
        row?.hex_code || merged.hex_code || merged.family_line_hex_code || "";
      return merged;
    },
    [rosterById]
  );

  const yearOptions = useMemo(() => {
    const current = now.getFullYear();
    const years = [];
    for (let y = current; y <= current + YEAR_PICKER_SPAN; y++) {
      years.push(y);
    }
    if (!years.includes(year)) years.push(year);
    return years.sort((a, b) => a - b).map((y) => ({ value: y, label: String(y) }));
  }, [now, year]);

  const flatRows = useMemo(
    () =>
      sections.flatMap((section) =>
        section.rows.map((row) =>
          resolveAnniversaryRow({
            ...row,
            years: row?.years ?? section.years,
            color_name: row?.color_name || section.colorName,
            hex_code: row?.hex_code || section.hexCode,
          })
        )
      ),
    [sections, resolveAnniversaryRow]
  );

  const filteredRows = useMemo(
    () => filterDashboardMembersBySearch(flatRows, searchQuery, getCoupleSearchName),
    [flatRows, searchQuery]
  );

  const isSearching = searchQuery.trim().length > 0;

  const sortedColorGroups = useMemo(() => {
    if (!filteredRows.length) return [];

    const apiOrder = new Map(flatRows.map((row, index) => [row, index]));

    const sortRows = (rows) => {
      const sorted = [...rows];

      if (sortBy === "alpha-asc") {
        return sorted.sort((a, b) => getCoupleSortName(a).localeCompare(getCoupleSortName(b)));
      }
      if (sortBy === "alpha-desc") {
        return sorted.sort((a, b) => getCoupleSortName(b).localeCompare(getCoupleSortName(a)));
      }
      // Line Color: each colour group's couples A–Z.
      if (sortBy === "color") {
        return sorted.sort((a, b) => getCoupleSortName(a).localeCompare(getCoupleSortName(b)));
      }
      if (sortBy === "date-asc" || sortBy === "date-desc") {
        const descending = sortBy === "date-desc";
        return sorted.sort((a, b) => {
          const monthA = getAnniversaryMonthNum(a);
          const monthB = getAnniversaryMonthNum(b);
          if (monthA !== monthB) {
            return descending ? monthB - monthA : monthA - monthB;
          }
          const dayA = getAnniversaryDayNum(a);
          const dayB = getAnniversaryDayNum(b);
          if (dayA === -1 && dayB === -1) return 0;
          if (dayA === -1) return 1;
          if (dayB === -1) return -1;
          return descending ? dayB - dayA : dayA - dayB;
        });
      }
      if (sortBy === "year-asc") {
        return sorted.sort((a, b) => {
          const yearsA = a?.years ?? -1;
          const yearsB = b?.years ?? -1;
          if (yearsA !== yearsB) return yearsA - yearsB;
          return (apiOrder.get(a) ?? 0) - (apiOrder.get(b) ?? 0);
        });
      }
      // year-desc and none
      return sorted.sort((a, b) => {
        const yearsA = a?.years ?? -1;
        const yearsB = b?.years ?? -1;
        if (yearsA !== yearsB) return yearsB - yearsA;
        return (apiOrder.get(a) ?? 0) - (apiOrder.get(b) ?? 0);
      });
    };

    const groupsByColor = new Map();
    filteredRows.forEach((row) => {
      const colorKey = (row?.color_name || "").toLowerCase();
      if (!groupsByColor.has(colorKey)) {
        groupsByColor.set(colorKey, {
          colorName: row?.color_name || "",
          hexCode: row?.hex_code || "#000000",
          rows: [],
          firstIndex: apiOrder.get(row) ?? 0,
        });
      }
      groupsByColor.get(colorKey).rows.push(row);
    });

    const groups = [...groupsByColor.values()].map((group) => ({
      ...group,
      rows: sortRows(group.rows),
    }));

    if (sortBy === "color") {
      return groups.sort((a, b) =>
        a.colorName.toLowerCase().localeCompare(b.colorName.toLowerCase())
      );
    }

    return groups.sort((a, b) => {
      const byColor = a.colorName.toLowerCase().localeCompare(b.colorName.toLowerCase());
      if (byColor !== 0) return byColor;
      return a.firstIndex - b.firstIndex;
    });
  }, [filteredRows, flatRows, sortBy]);

  const sortedRows = useMemo(
    () => sortedColorGroups.flatMap((group) => group.rows),
    [sortedColorGroups]
  );

  const totalCount = isSearching ? sortedRows.length : reportTotal || sortedRows.length;

  const renderMemberRow = (anniversary) => {
    const { firstName, spouseFirstName } = getCoupleFirstNames(anniversary);
    const suffix = anniversary?.suffix || "";
    const specialName = anniversary?.special_name || "";
    const spouseSuffix = anniversary?.spouse_special_name || anniversary?.spouse_suffix || "";
    const spouseSpecialName = anniversary?.spouse_special_name || "";

    const hasSpouseSide = Boolean(
      (spouseFirstName || "").trim() || spouseSuffix || spouseSpecialName
    );
    const primaryNameParts = getAnniversaryPersonNameParts(anniversary, {
      firstName,
      suffix,
      specialName,
      side: "primary",
      hasSpouseSide,
    });
    const spouseNameParts = getAnniversaryPersonNameParts(anniversary, {
      firstName: spouseFirstName,
      suffix: spouseSuffix,
      specialName: spouseSpecialName,
      side: "spouse",
      hasSpouseSide,
    });
    const coupleTrailingName = getAnniversaryCoupleTrailingName(suffix, specialName);
    const showPrimaryInLawAsterisk =
      isDashboardInLawMember(anniversary) && !isExplicitDashboardStartOrTopOfLineMember(anniversary);
    const showSpouseInLawAsterisk =
      (anniversary?.spouse_is_in_laws === true ||
        anniversary?.spouse_is_in_laws === "true" ||
        anniversary?.spouse_is_in_laws === 1) &&
      !isExplicitDashboardStartOrTopOfLineMember(anniversary);

    // The descendant is the primary person unless they married in, in which case
    // it is the spouse; that name is bolded in the couple string.
    const primaryIsInLaw = isDashboardInLawMember(anniversary);
    const spouseIsInLaw =
      anniversary?.spouse_is_in_laws === true ||
      anniversary?.spouse_is_in_laws === "true" ||
      anniversary?.spouse_is_in_laws === 1;
    const boldSpouse = hasSpouseSide && primaryIsInLaw && !spouseIsInLaw;
    const boldPrimary = !boldSpouse;

    const weddingDate = getMilestoneWeddingDateDisplay(anniversary);
    const milestoneYears = anniversary?.years ?? anniversary?.years_married ?? "";

    return (
      <div className={`dashboard-detail-row ${isLargeView ? "large-view" : ""}`}>
        <div
          className={`dashboard-detail-name-col ${isLargeView ? "large-view" : ""}`}
          style={
            isCompactLayout
              ? { width: "auto", minWidth: nameColumnWidth }
              : { width: nameColumnWidth }
          }
        >
          <DashboardNameMarkerGroup>
            <span className="dashboard-detail-couple-wrap">
              <DashboardPhotoName
                {...getAnniversaryPersonPhotoUrls(anniversary)}
                onViewPhoto={openPhoto}
              >
                <span className={boldPrimary ? "milestone-wedding-descendant-name" : undefined}>
                  <DashboardMemberNameLabel
                    baseName={primaryNameParts.baseName}
                    descendantSpouseName={primaryNameParts.descendantSpouseName}
                    afterBaseName={
                      showPrimaryInLawAsterisk && primaryNameParts.descendantSpouseName ? (
                        <DashboardInLawAsterisk isLargeView={isLargeView} />
                      ) : null
                    }
                  />
                </span>
              </DashboardPhotoName>
              {showPrimaryInLawAsterisk && !primaryNameParts.descendantSpouseName && (
                <DashboardInLawAsterisk isLargeView={isLargeView} />
              )}
              {hasSpouseSide ? (
                <>
                  <span className="dashboard-detail-couple-joiner">&</span>
                  <DashboardPhotoName
                    {...getAnniversaryPersonPhotoUrls(anniversary)}
                    onViewPhoto={openPhoto}
                  >
                    <span className={boldSpouse ? "milestone-wedding-descendant-name" : undefined}>
                      <DashboardMemberNameLabel
                        baseName={spouseNameParts.baseName}
                        descendantSpouseName={spouseNameParts.descendantSpouseName}
                        afterBaseName={
                          showSpouseInLawAsterisk && spouseNameParts.descendantSpouseName ? (
                            <DashboardInLawAsterisk isLargeView={isLargeView} />
                          ) : null
                        }
                      />
                    </span>
                  </DashboardPhotoName>
                  {showSpouseInLawAsterisk && !spouseNameParts.descendantSpouseName && (
                    <DashboardInLawAsterisk isLargeView={isLargeView} />
                  )}
                </>
              ) : null}
              {coupleTrailingName && (
                <span className="dashboard-detail-couple-trailing">{coupleTrailingName}</span>
              )}
            </span>
          </DashboardNameMarkerGroup>
        </div>

        <div
          className={`dashboard-detail-years-col milestone-wedding-years-col ${
            isLargeView ? "large-view" : ""
          }`}
          style={{
            width: isCompactLayout ? "auto" : yearsColumnWidth,
            gridTemplateColumns: yearsNumColumnWidth,
          }}
        >
          <span className="dashboard-detail-years-num">{milestoneYears}</span>
        </div>

        <div
          className={`dashboard-detail-date-col milestone-wedding-date-col ${
            isLargeView ? "large-view" : ""
          }`}
          style={
            isCompactLayout
              ? { width: "auto", minWidth: dateColumnWidth }
              : { width: dateColumnWidth }
          }
        >
          <span className="milestone-wedding-date-text">{weddingDate}</span>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (sortedRows.length === 0 || loading) return;

    const fontSize = isLargeView ? "24px" : "18px";

    const tempDiv = document.createElement("div");
    tempDiv.style.position = "absolute";
    tempDiv.style.visibility = "hidden";
    tempDiv.style.whiteSpace = "nowrap";
    tempDiv.style.fontSize = fontSize;
    tempDiv.style.fontFamily = "inherit";
    document.body.appendChild(tempDiv);

    const chMeasure = document.createElement("div");
    chMeasure.style.position = "absolute";
    chMeasure.style.visibility = "hidden";
    chMeasure.style.fontSize = fontSize;
    chMeasure.style.fontFamily = "inherit";
    chMeasure.textContent = "0";
    document.body.appendChild(chMeasure);
    const chWidth = chMeasure.getBoundingClientRect().width;

    const dateMeasure = document.createElement("div");
    dateMeasure.style.position = "absolute";
    dateMeasure.style.visibility = "hidden";
    dateMeasure.style.fontSize = fontSize;
    dateMeasure.style.fontFamily = "inherit";
    document.body.appendChild(dateMeasure);

    let maxNameWidth = 0;
    let maxYearsNumWidth = 0;
    let maxDateWidth = 0;

    sortedRows.forEach((anniversary) => {
      const { firstName, spouseFirstName } = getCoupleFirstNames(anniversary);
      const suffix = anniversary?.suffix || "";
      const specialName = anniversary?.special_name || "";
      const spouseSuffix = anniversary?.spouse_special_name || anniversary?.spouse_suffix || "";
      const spouseSpecialName = anniversary?.spouse_special_name || "";

      const hasSpouseSide = Boolean(
        (spouseFirstName || "").trim() || spouseSuffix || spouseSpecialName
      );
      const primaryParts = getAnniversaryPersonNameParts(anniversary, {
        firstName,
        suffix,
        specialName,
        side: "primary",
        hasSpouseSide,
      });
      const spouseParts = getAnniversaryPersonNameParts(anniversary, {
        firstName: spouseFirstName,
        suffix: spouseSuffix,
        specialName: spouseSpecialName,
        side: "spouse",
        hasSpouseSide,
      });
      const coupleTrailingName = getAnniversaryCoupleTrailingName(suffix, specialName);

      tempDiv.innerHTML = "";
      const wrap = document.createElement("span");
      wrap.style.display = "inline-flex";
      wrap.style.alignItems = "baseline";
      wrap.style.flexWrap = "nowrap";
      wrap.style.gap = "0";
      tempDiv.appendChild(wrap);

      const appendAsterisk = () => {
        const ast = document.createElement("span");
        ast.textContent = "*";
        ast.style.fontWeight = "700";
        wrap.appendChild(ast);
      };
      const isStartOrTop = isExplicitDashboardStartOrTopOfLineMember(anniversary);
      const showPrimaryAsteriskMeasure =
        isDashboardInLawMember(anniversary) && !isStartOrTop;
      const showSpouseAsteriskMeasure =
        (anniversary?.spouse_is_in_laws === true ||
          anniversary?.spouse_is_in_laws === "true" ||
          anniversary?.spouse_is_in_laws === 1) &&
        !isStartOrTop;

      const appendSide = ({ baseName, descendantSpouseName }) => {
        const base = document.createElement("span");
        base.textContent = baseName;
        wrap.appendChild(base);
        if (!descendantSpouseName) return;
        const open = document.createElement("span");
        open.textContent = " (";
        wrap.appendChild(open);
        const spouse = document.createElement("strong");
        spouse.style.fontWeight = "700";
        spouse.textContent = descendantSpouseName;
        wrap.appendChild(spouse);
        const close = document.createElement("span");
        close.textContent = ")";
        wrap.appendChild(close);
      };

      appendSide(primaryParts);
      if (showPrimaryAsteriskMeasure) appendAsterisk();
      if (hasSpouseSide) {
        const amp = document.createElement("span");
        amp.textContent = "&";
        amp.className = "dashboard-detail-couple-joiner";
        wrap.appendChild(amp);
        appendSide(spouseParts);
        if (showSpouseAsteriskMeasure) appendAsterisk();
      }
      if (coupleTrailingName) {
        const trailingEl = document.createElement("span");
        trailingEl.className = "dashboard-detail-couple-trailing";
        trailingEl.style.whiteSpace = "pre";
        trailingEl.textContent = coupleTrailingName;
        wrap.appendChild(trailingEl);
      }
      const nameWidth = tempDiv.getBoundingClientRect().width;
      if (nameWidth > maxNameWidth) maxNameWidth = nameWidth;

      const milestoneYears = anniversary?.years ?? anniversary?.years_married ?? "";
      dateMeasure.style.fontSize = fontSize;
      dateMeasure.style.fontWeight = "900";
      dateMeasure.style.webkitTextStroke = "0.35px currentColor";
      dateMeasure.textContent = String(milestoneYears);
      const yearsNumWidth = dateMeasure.getBoundingClientRect().width;
      if (yearsNumWidth > maxYearsNumWidth) maxYearsNumWidth = yearsNumWidth;

      dateMeasure.style.fontWeight = "400";
      dateMeasure.style.webkitTextStroke = "";
      dateMeasure.style.fontSize = fontSize;
      dateMeasure.textContent = getMilestoneWeddingDateDisplay(anniversary);
      const dateWidth = dateMeasure.getBoundingClientRect().width;
      if (dateWidth > maxDateWidth) maxDateWidth = dateWidth;
    });

    document.body.removeChild(tempDiv);
    document.body.removeChild(chMeasure);
    document.body.removeChild(dateMeasure);

    const nameWidthInCh = Math.ceil(maxNameWidth / chWidth);
    const yearsNumWidthInCh = Math.ceil(maxYearsNumWidth / chWidth);
    const dateWidthInCh = Math.ceil(maxDateWidth / chWidth);
    // Desktop and phone size each column to exactly its longest entry, so the
    // 3ch gap is measured from that entry; large view keeps the ch-rounded widths.
    const isDesktopLayout = !isLargeView;

    setNameColumnWidth(
      isDesktopLayout ? `${Math.ceil(maxNameWidth)}px` : `${nameWidthInCh}ch`
    );
    setDateColumnWidth(
      isDesktopLayout ? `${Math.ceil(maxDateWidth)}px` : `${dateWidthInCh}ch`
    );
    setYearsNumColumnWidth(`${yearsNumWidthInCh}ch`);
    setYearsColumnWidth(
      isDesktopLayout ? `${Math.ceil(maxYearsNumWidth)}px` : `${yearsNumWidthInCh}ch`
    );
  }, [sortedRows, loading, isLargeView, isCompactLayout]);

  const legendLines = useMemo(
    () => buildDashboardColorLegendLines(rosterMembers, headsMap),
    [rosterMembers, headsMap]
  );

  const showColorLegend = isCompactLayout && legendLines.length > 0;
  // The footer stays up across a change of period, so only the first load holds
  // it back: on a phone it would otherwise show a short legend until the family
  // lines arrive.
  const legendPending = isCompactLayout && loading && legendLines.length === 0;

  const renderDirectionalSortButton = (label, ascKey, descKey, title) => (
    <button
      type="button"
      onClick={() => setSortBy(sortBy === ascKey ? descKey : ascKey)}
      className={`dashboard-detail-sort-btn ${
        sortBy === ascKey || sortBy === descKey ? "active" : ""
      }`}
      title={title}
    >
      {label}
      {sortBy === ascKey ? (
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ marginLeft: "4px" }}
        >
          <path d="M18 15l-6-6-6 6" />
        </svg>
      ) : sortBy === descKey ? (
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ marginLeft: "4px" }}
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      ) : (
        <span
          style={{
            marginLeft: "4px",
            display: "inline-flex",
            flexDirection: "column",
            lineHeight: 1,
            alignItems: "center",
          }}
        >
          <svg
            width="8"
            height="8"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ display: "block" }}
          >
            <path d="M18 15l-6-6-6 6" />
          </svg>
          <svg
            width="8"
            height="8"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ display: "block", marginTop: "-2px" }}
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </span>
      )}
    </button>
  );

  const leadingSortButtons = (
    <>
      <div className="dashboard-detail-period" role="group" aria-label="Report period">
        <DashboardPeriodDropdown
          label="Year"
          options={yearOptions}
          value={year}
          onChange={(next) => setYear(parseInt(next, 10))}
        />
        <span className="dashboard-detail-sort-divider" aria-hidden="true" />
      </div>
      {renderDirectionalSortButton("Date", "date-asc", "date-desc", "Sort by Date")}
      {renderDirectionalSortButton("Year", "year-desc", "year-asc", "Sort by Year")}
    </>
  );

  return (
    <div
      className={`dashboard-container anniversaries-this-week-page milestone-wedding-anniversaries-page dashboard-detail-page ${
        isLargeView ? "large-view-mode" : ""
      }`}
    >
      <div className="dash-box dashboard-detail-box">
        <div className="dashboard-detail-header">
          <div className="dashboard-detail-header-left">
            <DashboardBackButton />
            <h3 className="dashboard-detail-title">Milestone Wedding Anniv</h3>
            <div className="dashboard-detail-count">{totalCount}</div>
            {!loading && (
              <DashboardDetailSearchBox
                value={searchQuery}
                onChange={setSearchQuery}
                centreOnRef={listRef}
              />
            )}
            {!loading && (
              <DashboardDetailSortFilterBar
                sortBy={sortBy}
                onSortChange={setSortBy}
                showAgeSorts={false}
                showColorSort={false}
                showMemberFilters={false}
                defaultSort={DEFAULT_SORT}
                leadingSortButtons={leadingSortButtons}
              />
            )}
          </div>
        </div>

        {loading ? (
          <div className="dashboard-detail-empty-state">
            <p>Loading milestone anniversaries...</p>
          </div>
        ) : sortedRows.length === 0 ? (
          <div className="dashboard-detail-empty-state anniversaries-no-data-empty-state">
            {isSearching ? (
              <DashboardDetailSearchNoMatch query={searchQuery} noun="anniversaries" />
            ) : (
              <p>No data for this period.</p>
            )}
          </div>
        ) : (
          <div className="anniversaries-list dashboard-detail-list" ref={listRef}>
            {sortedColorGroups.map((group) => {
              const headRow = group.rows[0];
              const headOfLineName =
                getDashboardFamilyLineHeadDisplayName(headRow, headsMap) ||
                String(headRow?.head_name || "").trim() ||
                "Start/Top of Line";

              return (
              <React.Fragment key={group.colorName || "unknown"}>
                <div
                  className={`dashboard-detail-row dashboard-detail-header-row ${
                    isLargeView ? "large-view" : ""
                  }`}
                >
                  <div
                    className={`dashboard-detail-name-col ${isLargeView ? "large-view" : ""}`}
                    style={{ width: isCompactLayout ? "auto" : nameColumnWidth }}
                  >
                    <span className="milestone-wedding-color-heading dashboard-section-heading">
                      <DashboardPhotoHeadLine
                        name={headOfLineName}
                        hexCode={group.hexCode}
                        colorName={group.colorName ? `${group.colorName} Line` : ""}
                        imageUrl={getDashboardMemberPhotoUrl(headRow, "head_image")}
                        onViewPhoto={openPhoto}
                        member={headRow}
                        headsMap={headsMap}
                      />
                      <span className="milestone-wedding-color-count dashboard-section-heading-count">
                        ({group.rows.length})
                      </span>
                    </span>
                  </div>
                  {!isCompactLayout && (
                    <>
                      <div
                        className="dashboard-detail-years-col milestone-wedding-years-col"
                        style={{ width: yearsColumnWidth }}
                      />
                      <div
                        className="dashboard-detail-date-col"
                        style={{ width: dateColumnWidth }}
                      />
                    </>
                  )}
                </div>
                {group.rows.map((anniversary, index) => (
                  <div key={anniversary?.id || `${group.colorName}-${index}`}>
                    {renderMemberRow(anniversary)}
                  </div>
                ))}
              </React.Fragment>
              );
            })}
          </div>
        )}

        {!legendPending && (
          <div className="anniversaries-report-footer">
            <p className="anniversaries-gems-note">{ANNIVERSARY_GEMS_NOTE}</p>
            <div className="anniversaries-this-week-page in-law-footer">
              <DashboardMarkerLegend />
              {showColorLegend && <DashboardColorLegend lines={legendLines} />}
            </div>
          </div>
        )}
      </div>
      <DashboardPhotoModal photos={photoModalPhotos} onClose={closePhoto} />
      <DashboardPhotoTapHint />
    </div>
  );
}

export default MilestoneWeddingAnniversaries;
